import { I18TranslateFunc } from '../../../../i18n';
import { LanguageCode } from '../../../models/language';
import { PudoItem, WorkingTimePartlySeparated } from '../../../models/pudoItem';

export function formatStringPascalCase(words: string[]): string {
  const wordsFormatted = words.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );

  const wordsCombined = wordsFormatted.join(' ');
  return wordsCombined;
}

export function getTodaysWorkingHours(pudo: PudoItem) {
  const currentDayNum = new Date().getDay() + 1; // + 1 here to correspond with backend values (1 - Sunday, 7 - Saturday)
  const currentDayWorkingTime = pudo.working_time.filter(
    (wt) => wt.day_number === currentDayNum
  )[0];

  return currentDayWorkingTime?.open_time && currentDayWorkingTime?.close_time
    ? `${currentDayWorkingTime.open_time} - ${currentDayWorkingTime.close_time}`
    : '';
}

export function getAddressFormatted(pudo: PudoItem) {
  const streetFormatted = formatStringPascalCase(
    pudo.address.street.split(' ')
  );
  return [streetFormatted, pudo.address.building].filter(Boolean).join(', ');
}

export function decodeHtmlEntities(encodedString: string) {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = encodedString;
  return textArea.textContent;
}

export type WeekDays =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

// HARDCODE. WILL BE REMOVE AFTER BACKEND ADDS IT ON THEIR SIDE
const getWorkingHoursTranslations = (
  locale: LanguageCode,
  dayName: WeekDays
) => {
  const mapper: Record<LanguageCode, Record<WeekDays, string>> = {
    en: {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    },
    uk: {
      Monday: 'Пн',
      Tuesday: 'Вт',
      Wednesday: 'Ср',
      Thursday: 'Чт',
      Friday: 'Пт',
      Saturday: 'Сб',
      Sunday: 'Нд',
    },
    ru: {
      Monday: 'Пн',
      Tuesday: 'Вт',
      Wednesday: 'Ср',
      Thursday: 'Чт',
      Friday: 'Пт',
      Saturday: 'Сб',
      Sunday: 'Вс',
    },
    it: {
      Monday: 'Lun',
      Tuesday: 'Mar',
      Wednesday: 'Mer',
      Thursday: 'Gio',
      Friday: 'Ven',
      Saturday: 'Sab',
      Sunday: 'Dom',
    },
    pl: {
      Monday: 'Pon',
      Tuesday: 'Wto',
      Wednesday: 'Śro',
      Thursday: 'Czw',
      Friday: 'Pią',
      Saturday: 'Sob',
      Sunday: 'Nie',
    },
    fr: {
      Monday: 'Lun',
      Tuesday: 'Mar',
      Wednesday: 'Mer',
      Thursday: 'Jeu',
      Friday: 'Ven',
      Saturday: 'Sam',
      Sunday: 'Dim',
    },
    de: {
      Monday: 'Mo',
      Tuesday: 'Di',
      Wednesday: 'Mi',
      Thursday: 'Do',
      Friday: 'Fr',
      Saturday: 'Sa',
      Sunday: 'So',
    },
    es: {
      Monday: 'Lu',
      Tuesday: 'Ma',
      Wednesday: 'Mi',
      Thursday: 'Ju',
      Friday: 'Vi',
      Saturday: 'Sá',
      Sunday: 'Do',
    },
  };
  return mapper[locale][dayName];
};

export function parseWorkingHoursSummary(
  workingTime: WorkingTimePartlySeparated[],
  t: I18TranslateFunc,
  locale: LanguageCode
) {
  const parsedSummary: {
    daysRange: string;
    timeRange: string;
    isClosed: boolean;
  }[] = [];
  [...workingTime]
    .sort((a, b) => {
      if (a.operating_hours.closed === b.operating_hours.closed) return 0;
      return a.operating_hours.closed ? 1 : -1;
    })
    .forEach((wt) => {
      let daysRange = '';
      let timeRange = '';

      const beginDayName = getWorkingHoursTranslations(
        locale,
        wt.begin.day_name
      );
      const endDayName = getWorkingHoursTranslations(locale, wt.end.day_name);
      if (wt.one_day_period) {
        daysRange = beginDayName;
      } else {
        daysRange = `${beginDayName}-${endDayName}`;
      }

      if (wt.operating_hours?.open_time && wt.operating_hours?.close_time) {
        timeRange = `${wt.operating_hours.open_time} - ${wt.operating_hours.close_time}`;
      } else if (wt.operating_hours.closed) {
        timeRange = t('closed');
      } else if (wt.operating_hours.open_24_hours_indicator) {
        timeRange = '00:01 - 23:59';
      }

      parsedSummary.push({
        daysRange,
        timeRange,
        isClosed: wt.operating_hours.closed,
      });
    });

  return parsedSummary;
}
